import { ArrowUpRight, Download, FileText, Link as LinkIcon } from "lucide-react";
import { BaseSheet } from "@/components/common/BaseSheet";
import { useIsMobile } from "@/hooks/ui/useIsMobile";
import { useFoundMedia } from "@/hooks/voice/useFoundMedia";
import type { InfoLinksConfig } from "@/types/agent/info-links";
import { isSafeUrl, LINK_ICON_MAP } from "./infoLinkIcons";

type ResourcesSheetProps = {
  open: boolean;
  onClose: () => void;
  brandColor: string;
  resources?: InfoLinksConfig | null;
};

const formatSize = (bytes?: number): string | null => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const hostnameOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

export const ResourcesSheet =({ open, onClose, brandColor, resources }: ResourcesSheetProps) => {
  const isMobile = useIsMobile(768);

  const foundMedia = useFoundMedia();

  const documents = (resources?.documents ?? []).filter((d) => d.fileUrl && isSafeUrl(d.fileUrl));
  const links = (resources?.links ?? []).filter((l) => isSafeUrl(l.url));
  const listedUrls = new Set([...documents.map((doc) => doc.fileUrl), ...links.map((link) => link.url)]);
  const found = foundMedia.filter((item) => !listedUrls.has(item.url) && isSafeUrl(item.pageUrl ?? item.url));

  const hasAny = documents.length > 0 || links.length > 0 || found.length > 0;

  return (
    <BaseSheet
      isOpen={open}
      onClose={onClose}
      title="Resources"
      description="Documents and links to take away from this conversation."
      side={isMobile ? "bottom" : "right"}
      size={isMobile ? "full" : "md"}
      overlayClassName="z-[11990]"
      contentClassName="z-[12000]"
    >
      {!hasAny ? (
        <p className="text-sm leading-relaxed text-[#7C8B9C]" role="note">
          Nothing here yet.
        </p>
      ) : (
        <div className="space-y-7">
          {documents.length > 0 && (
            <section>
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9AA7B6]">
                Documents
              </h3>
              <div className="space-y-2">
                {documents.map((doc) => {
                  const size = formatSize(doc.sizeBytes);
                  return (
                    <a
                      key={doc.id}
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName || undefined}
                      className="group flex items-center gap-3 rounded-xl border border-[#E9EDF3] bg-white p-3 transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
                    >
                      <span
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${brandColor}14`, color: brandColor }}
                      >
                        <FileText size={18} strokeWidth={2} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#222B45]">{doc.label || doc.fileName}</span>
                        {size && <span className="block text-xs text-[#9AA7B6]">{size}</span>}
                      </span>
                      <span
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors"
                        style={{ color: brandColor }}
                        aria-hidden
                      >
                        <Download size={16} strokeWidth={2.25} />
                      </span>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {links.length > 0 && (
            <section>
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9AA7B6]">Links</h3>
              <div className="space-y-2">
                {links.map((link) => {
                  const Icon = LINK_ICON_MAP[link.icon ?? ""] ?? LINK_ICON_MAP.link;
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-xl border border-[#E9EDF3] bg-white p-3 transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
                    >
                      <span
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${brandColor}14`, color: brandColor }}
                      >
                        <Icon size={18} />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#222B45]">{link.label}</span>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {found.length > 0 && (
            <section>
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9AA7B6]">
                Found in this conversation
              </h3>
              <div className="space-y-2">
                {found.map((item) => {
                  const href = item.kind === "image" ? (item.pageUrl ?? item.url) : item.url;
                  const thumbnail = item.kind === "image" ? item.url : item.imageUrl;
                  return (
                    <a
                      key={item.url}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-xl border border-[#E9EDF3] bg-white p-3 transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
                    >
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt=""
                          loading="lazy"
                          className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <span
                          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                          style={{ backgroundColor: `${brandColor}14`, color: brandColor }}
                        >
                          <LinkIcon size={18} />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#222B45]">{item.title}</span>
                        <span className="block truncate text-xs text-[#9AA7B6]">{hostnameOf(href)}</span>
                      </span>
                      <span
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                        style={{ color: brandColor }}
                        aria-hidden
                      >
                        <ArrowUpRight size={16} strokeWidth={2.25} />
                      </span>
                    </a>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </BaseSheet>
  );
};

export default ResourcesSheet;
